import { Alert, Button, NumberInput, Stack, TextInput } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@apollo/client';
import { addVariantToProductDocument } from '../graphql/mutations';

interface FormValues {
  stock: number;
  attributes: { name: string; value: string }[];
}

interface Props {
  productId: string;
  existingAttributeNames: string[];
}

export const AddVariantModal = ({ productId, existingAttributeNames }: Props) => {
  const hasSchema = existingAttributeNames.length > 0;

  const form = useForm<FormValues>({
    initialValues: {
      stock: 1,
      attributes: hasSchema
        ? existingAttributeNames.map((name) => ({ name, value: '' }))
        : [{ name: '', value: '' }],
    },
    validate: {
      stock: (value) => (value < 0 ? 'Sklad nemôže byť záporný' : null),
      attributes: {
        name: isNotEmpty('Názov atribútu je povinný'),
        value: isNotEmpty('Hodnota atribútu je povinná'),
      },
    },
  });

  const [addVariant, { loading }] = useMutation(addVariantToProductDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    notifyOnNetworkStatusChange: true,
  });

  const handleSubmit = async (values: FormValues) => {
    await addVariant({
      variables: {
        productId,
        stock: values.stock,
        attributes: values.attributes.map(({ name, value }) => ({ name, value })),
      },
    });

    notifications.show({ message: 'Variant pridaný', color: 'green' });
    modals.closeAll();
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        {!hasSchema && (
          <Alert variant="light" color="orange">
            Produkt nemá žiadny existujúci variant. Najprv pridajte variant cez úpravu produktu.
          </Alert>
        )}
        {form.values.attributes.map((attr, index) => (
          <TextInput
            key={index}
            label={hasSchema ? attr.name : undefined}
            placeholder={hasSchema ? `Hodnota pre ${attr.name}` : 'Hodnota atribútu'}
            {...form.getInputProps(`attributes.${index}.value`)}
          />
        ))}
        <NumberInput
          label="Sklad"
          allowNegative={false}
          allowDecimal={false}
          hideControls
          {...form.getInputProps('stock')}
        />
        <Button type="submit" loading={loading} disabled={!hasSchema}>
          Uložiť variantu
        </Button>
      </Stack>
    </form>
  );
};
